import { PenIcon } from "@xola/icons";
import { Skeleton } from "@xola/ui-kit";
import { isEquipmentBased } from "common";
import React, { useState } from "react";
import { useMutation } from "react-query";
import { Experience, ProductObject, Resource, ScheduleType, SelectedExperiences, SelectionState } from "types";
import { api } from "../../../api/api";
import { useAutoFetchCollection } from "../../../hooks/useAutoFetchCollection";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { getSelectedExperiences, getSelectionState } from "../../../models/SelectionState";
import { ProductSelectorModal } from "../../Common/ProductSelectorModal";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { SelectedProductList } from "../../Common/SelectedProductList";
import { Loader } from "../../Loader";

export const ListingAssignmentModal = ({ resource }: { resource: Resource }) => {
    const { data: experiences, isFetching } = useAutoFetchCollection<Experience>("experiences");
    const [showExperienceSelectorModal, setShowExperienceSelectorModal] = useState(false);
    const invalidate = useInvalidate();

    const { mutateAsync: updateProducts, isLoading: isUpdating } = useMutation(
        async ({ assign, unassign }: { assign: ID[]; unassign: ID[] }) => {
            // We have to make calls sequentially here to avoid parallel API calls,
            // because otherwise a bug occurs when jobs for the seller can end up being processed at the same time.
            for (const experienceId of unassign) {
                await unassignProduct(resource.id, experienceId);
            }

            for (const experienceId of assign) {
                await assignProduct(resource.id, experienceId);
            }
        },
        {
            onSuccess: () => {
                invalidate("resources", resource.id);
                invalidate("experiences");
            },
        },
    );

    const handleConfirmSelectedExperiences = (updatedSelection: SelectedExperiences) => {
        let experienceIdsToAssign: ID[];
        let experienceIdsToUnAssign: ID[] = [];
        const allExperienceIds = experiences
            .filter((experience) => experience.scheduleType !== ScheduleType.OPEN && !isEquipmentBased(experience))
            .map((experience) => experience.id);

        if (updatedSelection.all) {
            experienceIdsToAssign = allExperienceIds;
        } else {
            const lastExps = selectedExperiences.all ? allExperienceIds : selectedExperiences.experiences;
            experienceIdsToAssign = updatedSelection.experiences.filter((id) => !lastExps.includes(id));
            experienceIdsToUnAssign = lastExps.filter((id) => !updatedSelection.experiences.includes(id));
        }

        updateProducts({
            assign: experienceIdsToAssign,
            unassign: experienceIdsToUnAssign,
        });
    };

    if (!resource) {
        return <Loader />;
    }

    const equipmentDisabledReasons = Object.fromEntries(
        experiences
            .filter((experience) => isEquipmentBased(experience))
            .map((experience) => [
                experience.id,
                "Because you have configured this as an equipment-based pricing experience, you must manage the equipment by editing the experience.",
            ]),
    );

    const resourceExperiences = experiences.filter((experience) =>
        experience.resources.some((value) => value.id === resource.id),
    );

    const selectedExperiences: SelectedExperiences =
        resourceExperiences.length === experiences.length
            ? { all: true, experiences: [] }
            : {
                  all: false,
                  experiences: resourceExperiences?.map((experience) => experience.id),
              };

    return (
        <>
            <div className="mb-5 flex items-center justify-between">
                {/* TODO: Update to use SelectedProductListInput */}
                <h3>Products</h3>
                <SecondaryButton
                    size="small"
                    disabled={isFetching || isUpdating}
                    data-testid="listing-assignment-modal-open-button"
                    onClick={() => setShowExperienceSelectorModal(true)}
                >
                    <PenIcon />
                </SecondaryButton>
            </div>

            {isUpdating || isFetching ? (
                <Skeleton height={160} />
            ) : (
                <SelectedProductList selectedProducts={getSelectionState(selectedExperiences)} />
            )}

            <ProductSelectorModal
                allowEmptySelection
                hideUnScheduled
                value={getSelectionState(selectedExperiences)}
                isOpen={showExperienceSelectorModal}
                title="Product Restrictions"
                description="Restrict the products where this equipment is used"
                objects={[ProductObject.EXPERIENCE]}
                disabledReasons={equipmentDisabledReasons}
                onClose={() => setShowExperienceSelectorModal(false)}
                onConfirm={(value: SelectionState) => handleConfirmSelectedExperiences(getSelectedExperiences(value))}
            />
        </>
    );
};

const assignProduct = async (resource: ID, experience: ID) => {
    return api.post(`/experiences/${experience}/resources`, { resource: { id: resource }, priority: 0 });
};

const unassignProduct = async (resource: ID, experience: ID) => {
    return api.delete(`/experiences/${experience}/resources/${resource}`);
};
